import React from 'react';
import { Users, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MICROCOPY } from '../../constants/microcopy';
import { NAV_LABELS } from '../../constants/uiCopy';

const DashboardModals = ({
  user,
  // Commission modal
  showCommissionModal, setShowCommissionModal,
  newCommission, setNewCommission, createCommission,
  // Lead modal
  showLeadModal, setShowLeadModal,
  newLead, setNewLead, createLead,
  marketplaceProperties, managedProperties,
  // Sale confirmation modal
  showConfirmSaleModal, setShowConfirmSaleModal,
  selectedSaleNotification, saleConfirmation, setSaleConfirmation, confirmSale,
  brokerProfile, reserveUnit, releaseUnit,
  // Appointment edit
  editingAppointment, setEditingAppointment,
  editForm, setEditForm, handleEditAppointment
}) => {
  return (
    <>
      {/* Commission Modal */}
      {showCommissionModal && user?.role === 'broker' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg max-w-xl w-full">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">Registrar Nueva Comisión</h2>
              <button
                aria-label="Cerrar modal de comisión"
                onClick={() => setShowCommissionModal(false)}
                className="p-1 hover:bg-muted rounded"
              >
                X
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">{MICROCOPY.dashboardModals.commission}</p>
              <Input placeholder="Título de Propiedad *" value={newCommission.property_title} onChange={(e) => setNewCommission({ ...newCommission, property_title: e.target.value })} />
              <div className="grid grid-cols-2 gap-4">
                <Input type="number" placeholder="Monto de Venta *" value={newCommission.sale_amount} onChange={(e) => setNewCommission({ ...newCommission, sale_amount: e.target.value })} />
                <Input type="number" step="0.1" placeholder="% Comisión *" value={newCommission.commission_percentage} onChange={(e) => setNewCommission({ ...newCommission, commission_percentage: e.target.value })} />
              </div>
              {newCommission.sale_amount && newCommission.commission_percentage && (
                <p className="text-sm text-muted-foreground">
                  Comisión estimada: ${(parseFloat(newCommission.sale_amount) * parseFloat(newCommission.commission_percentage) / 100).toLocaleString()} MXN
                </p>
              )}
              <Input type="date" value={newCommission.closing_date} onChange={(e) => setNewCommission({ ...newCommission, closing_date: e.target.value })} />
              <textarea placeholder="Notas (opcional)" value={newCommission.notes} onChange={(e) => setNewCommission({ ...newCommission, notes: e.target.value })} rows={3} className="w-full border border-border rounded-md px-3 py-2 text-sm" />
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCommissionModal(false)} className="flex-1">Cancelar</Button>
                <Button onClick={createCommission} disabled={!newCommission.property_title || !newCommission.sale_amount} className="flex-1">Registrar</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Modal */}
      {showLeadModal && user?.role === 'broker' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg max-w-xl w-full shadow-xl">
            <div className="p-5 border-b border-border flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, \'Segoe UI\', sans-serif' }}>Registrar Cliente Potencial</h2>
                <p className="text-sm text-muted-foreground mt-1">Añade un nuevo lead a tu pipeline</p>
              </div>
              <button
                aria-label="Cerrar modal de lead"
                onClick={() => setShowLeadModal(false)}
                className="p-2 hover:bg-muted rounded-full"
              >
                X
              </button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-muted-foreground">{MICROCOPY.dashboardModals.lead}</p>
              <div>
                <label className="text-sm font-medium mb-2 block">Nombre del Cliente <span className="text-destructive">*</span></label>
                <Input placeholder="Ej: Juan Perez" value={newLead.client_name} onChange={(e) => setNewLead({ ...newLead, client_name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Teléfono</label>
                  <Input type="tel" placeholder="55 1234 5678" value={newLead.client_phone} onChange={(e) => setNewLead({ ...newLead, client_phone: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Email</label>
                  <Input type="email" placeholder="cliente@email.com" value={newLead.client_email} onChange={(e) => setNewLead({ ...newLead, client_email: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Propiedad de Interes (Opcional)</label>
                <select value={newLead.property_id} onChange={(e) => setNewLead({ ...newLead, property_id: e.target.value })} className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm">
                  <option value="">Sin asignar (lead general)</option>
                  {marketplaceProperties.length > 0 && (
                    <optgroup label={`Propiedades Disponibles en ${NAV_LABELS.collaborationInventory}`}>
                      {marketplaceProperties.map((prop) => (
                        <option key={prop.id} value={prop.id}>{prop.title} - ${prop.price_mxn?.toLocaleString()} MXN</option>
                      ))}
                    </optgroup>
                  )}
                  {managedProperties.length > 0 && (
                    <optgroup label="Propiedades que Gestiono">
                      {managedProperties.map((prop) => (
                        <option key={prop.id} value={prop.id}>{prop.title}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Fuente del contacto</label>
                <select value={newLead.source} onChange={(e) => setNewLead({ ...newLead, source: e.target.value })} className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm">
                  <option value="directo">Contacto Directo</option>
                  <option value="referido">Referido</option>
                  <option value="web">Sitio Web</option>
                  <option value="redes">Redes Sociales</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Notas</label>
                <textarea placeholder="Presupuesto, preferencias..." value={newLead.notes} onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })} rows={3} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowLeadModal(false)} className="flex-1">Cancelar</Button>
                <Button onClick={createLead} disabled={!newLead.client_name} className="flex-1">Registrar Lead</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Sale Modal */}
      {showConfirmSaleModal && selectedSaleNotification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg max-w-xl w-full shadow-xl">
            <div className="p-5 border-b border-border flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Confirmar Venta</h2>
                <p className="text-sm text-muted-foreground mt-1">Registra el cierre de esta operación</p>
              </div>
              <button
                aria-label="Cerrar modal de confirmación de venta"
                onClick={() => setShowConfirmSaleModal(false)}
                className="p-2 hover:bg-muted rounded-full"
              >
                X
              </button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-sm text-muted-foreground">{MICROCOPY.dashboardModals.sale}</p>
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <p className="text-sm"><span className="text-muted-foreground">Cliente:</span> <span className="font-semibold">{selectedSaleNotification.client_name}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Propiedad:</span> <span className="font-semibold">{selectedSaleNotification.item_title}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Broker:</span> <span className="font-semibold">{selectedSaleNotification.broker_name}</span></p>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Precio de Venta <span className="text-muted-foreground">(opcional)</span></label>
                <Input type="number" placeholder="Ej: 3,500,000" value={saleConfirmation.sale_price} onChange={(e) => setSaleConfirmation({ ...saleConfirmation, sale_price: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Notas <span className="text-muted-foreground">(opcional)</span></label>
                <textarea placeholder="Detalles adicionales..." value={saleConfirmation.notes} onChange={(e) => setSaleConfirmation({ ...saleConfirmation, notes: e.target.value })} rows={3} className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowConfirmSaleModal(false)} className="flex-1">Cancelar</Button>
                <Button onClick={confirmSale} className="flex-1">Confirmar Venta</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Appointment Dialog */}
      <Dialog open={!!editingAppointment} onOpenChange={(open) => !open && setEditingAppointment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Propuesta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{MICROCOPY.dashboardModals.appointment}</p>
            <div>
              <label className="text-sm font-medium mb-2 block">Fecha</label>
              <Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Hora</label>
              <select value={editForm.time_slot} onChange={(e) => setEditForm({ ...editForm, time_slot: e.target.value })} className="w-full h-10 px-3 border border-input bg-background rounded-md">
                {['09:00','10:00','11:00','12:00','14:00','15:00','16:00','17:00'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Unidad</label>
              <Input value={editForm.unit_number} onChange={(e) => setEditForm({ ...editForm, unit_number: e.target.value })} placeholder="Ej: A-101" />
            </div>
            <Button onClick={handleEditAppointment} className="w-full">Guardar Cambios</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { DashboardModals };
